import { type FC, type FormEvent, useState } from 'react';

import { Button, FormHelperText } from '@selfinspectionorg/ui-kit';

import { type IAccountView } from '@shared/services';
import styles from './SelectAccountStep.module.scss';
import { accountHasLocationChoices } from './accountHasLocationChoices';
import { LoginAccount } from './components/LoginAccount';
import { LoginLocation } from './components/LoginLocation';
import { Card } from '@/components/ui';

export interface AccountSelection {
  userId: number;
  locationId?: number;
}

interface Props {
  accounts: IAccountView[];
  onConfirm: (selected: AccountSelection) => void;
  /** Returns to the credential form. Discards any held accounts-proof (a fresh sign-in is required). */
  onBack: () => void;
  isConfirming?: boolean;
  error?: string | null;
}

type PickerSelection = { kind: 'account'; userId: number } | { kind: 'location'; userId: number; locationId: number };

/**
 * The account picker as an in-page step of the login wizard (replacing the former modal). It renders
 * inside the same `Card` chrome as the credential and MFA steps, keeping the account/location
 * selection sub-UI (`LoginAccount` / `LoginLocation`) unchanged.
 */
export const SelectAccountStep: FC<Props> = ({ accounts, onConfirm, onBack, isConfirming = false, error }) => {
  const [selection, setSelection] = useState<PickerSelection | null>(null);

  const handleConfirm = (event: FormEvent) => {
    event.preventDefault();

    if (!selection || isConfirming) {
      return;
    }

    const confirmed =
      selection.kind === 'location'
        ? { userId: selection.userId, locationId: selection.locationId }
        : { userId: selection.userId };

    onConfirm(confirmed);
  };

  return (
    <Card className={styles.wrapper}>
      <Card.BackButton onClick={onBack} testId="select-account-back-button" />
      <Card.Title title="Select Account" description="The specified email has multiple accounts" />

      <form noValidate onSubmit={handleConfirm} className={styles.accountList} id="selectAccountForm">
        {accounts.map((account, index) => {
          const showLocationChoices = accountHasLocationChoices(account);
          const isAccountSelected = selection?.kind === 'account' && selection.userId === account.id;

          return (
            <div key={account.id} className={styles.accountGroup}>
              <LoginAccount
                userId={account.id}
                companyName={account.companyName}
                role={account.role}
                showRadio={!showLocationChoices}
                isSelected={isAccountSelected}
                onChange={() => setSelection({ kind: 'account', userId: account.id })}
              />
              {showLocationChoices && (
                <div className={styles.locations}>
                  {account.locations.map((location) => (
                    <LoginLocation
                      key={location.id}
                      location={location}
                      accountId={account.id}
                      isSelected={
                        selection?.kind === 'location' &&
                        selection.userId === account.id &&
                        selection.locationId === location.id
                      }
                      onChange={() => setSelection({ kind: 'location', userId: account.id, locationId: location.id })}
                    />
                  ))}
                </div>
              )}
              {index !== accounts.length - 1 && <div className={styles.divider} />}
            </div>
          );
        })}
      </form>

      {error && <FormHelperText errored>{error}</FormHelperText>}

      <Button
        variant="primary"
        type="submit"
        form="selectAccountForm"
        width="100%"
        disabled={!selection || isConfirming}
        isLoading={isConfirming}
      >
        Log In
      </Button>
    </Card>
  );
};
